import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  Grid,
  Badge,
} from '@mui/material';
import {
  AddOutlined,
  LockOpenOutlined,
  GavelOutlined,
  SwapHorizOutlined,
  ReplayOutlined,
  DeleteOutline,
  WarningAmberOutlined,
  AttachFileOutlined,
} from '@mui/icons-material';
import Selector from '../../../../components/Selector';
import GarantiaAdjuntos from './GarantiaAdjuntos';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { categoriasGarantia } from '../../../../utils/staticvariables';
import usePermissions from '../../../../hooks/usePermissions';
import {
  EstadoChip,
  CategoryChip,
  SectionHeader,
  StatCard,
  ESTADO_META,
  parseGarantia,
  garantiaLabel,
  camposDeCategoria,
} from './garantiasUI';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const ESTADO_ACCIONES = {
  liberada: { label: 'Liberar', verbo: 'liberar', icon: <LockOpenOutlined fontSize="small" />, color: 'success' },
  ejecutada: { label: 'Ejecutar', verbo: 'ejecutar', icon: <GavelOutlined fontSize="small" />, color: 'error' },
  activa: { label: 'Reactivar', verbo: 'reactivar', icon: <ReplayOutlined fontSize="small" />, color: 'info' },
};

const TH_SX = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' };

/**
 * Manejo de garantías embebido en el detalle de un préstamo.
 * Lista las garantías de la solicitud del préstamo y permite el ciclo de vida
 * completo (liberar / ejecutar / sustituir / reactivar / eliminar) y agregar nuevas.
 */
export default function GarantiasPrestamo({ idPrestamo }) {
  const { hasPermission } = usePermissions();
  const puedeCrear = hasPermission('garantias', 'crear');
  const puedeEditar = hasPermission('garantias', 'editar');
  const puedeEliminar = hasPermission('garantias', 'eliminar');

  const [garantias, setGarantias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState(null);

  // Estado dialog (liberar / ejecutar / reactivar)
  const [estadoDialog, setEstadoDialog] = useState({ open: false, garantia: null, nuevoEstado: '' });
  const [motivo, setMotivo] = useState('');
  const [savingEstado, setSavingEstado] = useState(false);

  // Form dialog (agregar / sustituir)
  const [formDialog, setFormDialog] = useState({ open: false, mode: 'agregar', garantia: null });
  const [formCategoria, setFormCategoria] = useState('');
  const [formCampos, setFormCampos] = useState({});
  const [formMotivo, setFormMotivo] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [savingForm, setSavingForm] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Adjuntos (documentos / fotos)
  const [adjuntosTarget, setAdjuntosTarget] = useState(null);

  const fetchGarantias = useCallback(async () => {
    if (!idPrestamo) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/prestamo/${idPrestamo}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      setGarantias(Array.isArray(data) ? data : []);
    } catch (err) {
      setActionError('No se pudieron cargar las garantías');
    } finally {
      setLoading(false);
    }
  }, [idPrestamo]);

  useEffect(() => {
    fetchGarantias();
  }, [fetchGarantias]);

  // --- Estado (liberar / ejecutar / reactivar) ---
  const openEstado = (garantia, nuevoEstado) => {
    setMotivo('');
    setEstadoDialog({ open: true, garantia, nuevoEstado });
  };
  const closeEstado = () => {
    if (savingEstado) return;
    setEstadoDialog({ open: false, garantia: null, nuevoEstado: '' });
  };
  const submitEstado = async () => {
    if (!estadoDialog.garantia) return;
    setSavingEstado(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${estadoDialog.garantia.id}/estado`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ estado: estadoDialog.nuevoEstado, motivo }) }
      );
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.error || 'No se pudo cambiar el estado');
      }
      closeEstado();
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSavingEstado(false);
    }
  };

  // --- Form (agregar / sustituir) ---
  const openForm = (mode, garantia = null) => {
    setFormCategoria('');
    setFormCampos({});
    setFormMotivo('');
    setFormErrors({});
    setFormDialog({ open: true, mode, garantia });
  };
  const closeForm = () => {
    if (savingForm) return;
    setFormDialog({ open: false, mode: 'agregar', garantia: null });
  };
  const handleFormField = (campo, value) => {
    const clean = campo.type === 'monto' ? value.replace(/[^0-9.]/g, '') : value;
    setFormCampos(prev => ({ ...prev, [campo.id]: clean }));
    setFormErrors(prev => ({ ...prev, [campo.id]: undefined }));
  };
  const submitForm = async () => {
    const errs = {};
    if (!formCategoria) errs.categoria = 'Selecciona una categoría';
    camposDeCategoria(formCategoria).forEach(campo => {
      if (campo.required && !formCampos[campo.id]) errs[campo.id] = `${campo.name} es requerido`;
    });
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }
    setSavingForm(true);
    setActionError(null);
    try {
      const url = formDialog.mode === 'sustituir'
        ? `${process.env.REACT_APP_SERVER_URL}/api/garantias/${formDialog.garantia.id}/sustituir`
        : `${process.env.REACT_APP_SERVER_URL}/api/garantias/prestamo/${idPrestamo}`;
      const body = formDialog.mode === 'sustituir'
        ? { categoria: formCategoria, garantia: formCampos, motivo: formMotivo }
        : { categoria: formCategoria, garantia: formCampos };
      const response = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.error || 'No se pudo guardar la garantía');
      }
      closeForm();
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSavingForm(false);
    }
  };

  // --- Delete ---
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${deleteTarget.id}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.error || 'No se pudo eliminar la garantía');
      }
      setDeleteTarget(null);
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const stats = {
    total: garantias.length,
    activa: garantias.filter(g => (g.estado || 'activa') === 'activa').length,
    liberada: garantias.filter(g => g.estado === 'liberada').length,
    ejecutada: garantias.filter(g => g.estado === 'ejecutada').length,
  };

  return (
    <Box>
      <SectionHeader
        title="Garantías del préstamo"
        action={puedeCrear ? (
          <Button size="small" variant="contained" startIcon={<AddOutlined />} onClick={() => openForm('agregar')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Agregar garantía
          </Button>
        ) : null}
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : garantias.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Este préstamo no tiene garantías registradas.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item size={{ xs: 6, sm: 3 }}><StatCard label="Total" value={stats.total} accent="#18586b" /></Grid>
            <Grid item size={{ xs: 6, sm: 3 }}><StatCard label="Activas" value={stats.activa} accent={ESTADO_META.activa.dot} /></Grid>
            <Grid item size={{ xs: 6, sm: 3 }}><StatCard label="Liberadas" value={stats.liberada} accent={ESTADO_META.liberada.dot} /></Grid>
            <Grid item size={{ xs: 6, sm: 3 }}><StatCard label="Ejecutadas" value={stats.ejecutada} accent={ESTADO_META.ejecutada.dot} /></Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' } }}>
                  <TableCell sx={TH_SX}>Categoría</TableCell>
                  <TableCell sx={TH_SX}>Estado</TableCell>
                  <TableCell sx={TH_SX}>Detalle</TableCell>
                  <TableCell sx={TH_SX}>Valor</TableCell>
                  <TableCell sx={TH_SX}>Registro</TableCell>
                  <TableCell sx={TH_SX} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {garantias.map((g) => {
                  const data = parseGarantia(g);
                  const mainDetail = data.id || data.marca || data.descrip || 'N/A';
                  const valor = parseFloat(data.valor || 0);
                  const estado = g.estado || 'activa';
                  const sugerirEjecucion = g.prestamo_estado === 'irrecuperable' && estado === 'activa';
                  const sucesora = g.sustituida_por ? garantias.find(x => x.id === g.sustituida_por) : null;
                  const predecesora = garantias.find(x => x.sustituida_por === g.id);

                  return (
                    <TableRow key={g.id} hover sx={sugerirEjecucion ? { backgroundColor: '#fdecea' } : undefined}>
                      <TableCell><CategoryChip categoria={g.categoria} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <EstadoChip estado={estado} />
                          {sugerirEjecucion && (
                            <Tooltip title="Préstamo irrecuperable: se sugiere ejecutar la garantía">
                              <WarningAmberOutlined fontSize="small" color="error" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {mainDetail}
                        {sucesora && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Sustituida por: {garantiaLabel(sucesora)}
                          </Typography>
                        )}
                        {predecesora && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Sustituye a: {garantiaLabel(predecesora)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {valor > 0
                          ? <Typography variant="body2" fontWeight={700} color="success.main">${formatNumberWithCommas(valor.toFixed(2))}</Typography>
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{g.fecha_creacion || '—'}</Typography></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Documentos y fotos">
                            <IconButton size="small" onClick={() => setAdjuntosTarget(g)}>
                              <Badge badgeContent={g.adjuntos_count || 0} color="primary">
                                <AttachFileOutlined fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                          {puedeEditar && estado === 'activa' && (
                            <>
                              <Tooltip title="Liberar">
                                <IconButton size="small" color="success" onClick={() => openEstado(g, 'liberada')}>
                                  <LockOpenOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Ejecutar">
                                <IconButton size="small" color="error" onClick={() => openEstado(g, 'ejecutada')}>
                                  <GavelOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sustituir">
                                <IconButton size="small" color="warning" onClick={() => openForm('sustituir', g)}>
                                  <SwapHorizOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {puedeEditar && estado !== 'activa' && (
                            <Tooltip title="Reactivar">
                              <IconButton size="small" color="info" onClick={() => openEstado(g, 'activa')}>
                                <ReplayOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {puedeEliminar && (
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={() => setDeleteTarget(g)}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Estado dialog */}
      <Dialog open={estadoDialog.open} onClose={closeEstado} maxWidth="xs" fullWidth>
        <DialogTitle>
          {(ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.label || 'Cambiar estado')} garantía
        </DialogTitle>
        <Divider />
        <DialogContent>
          {estadoDialog.garantia && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {`¿Confirmas ${ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.verbo || 'cambiar'} la garantía `}
              <strong>{garantiaLabel(estadoDialog.garantia)}</strong>?
            </Typography>
          )}
          <TextField
            label="Motivo (opcional)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEstado} disabled={savingEstado}>Cancelar</Button>
          <Button
            variant="contained"
            color={ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.color || 'primary'}
            onClick={submitEstado}
            disabled={savingEstado}
            startIcon={savingEstado ? <CircularProgress size={16} color="inherit" /> : ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.icon}
          >
            {ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.label || 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form dialog (agregar / sustituir) */}
      <Dialog open={formDialog.open} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{formDialog.mode === 'sustituir' ? 'Sustituir garantía' : 'Agregar garantía'}</DialogTitle>
        <Divider />
        <DialogContent>
          {formDialog.mode === 'sustituir' && formDialog.garantia && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {`La garantía actual (${garantiaLabel(formDialog.garantia)}) quedará como sustituida. `}
              Registra abajo la garantía que la reemplaza.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item size={12} xs={12}>
              <Typography fontWeight={600} color="textSecondary" gutterBottom>Categoría</Typography>
              <Selector
                options={categoriasGarantia}
                value={formCategoria}
                onChange={(e) => { setFormCampos({}); setFormErrors({}); setFormCategoria(e.target.value); }}
                placeholder="Categoría de garantía"
              />
              {formErrors.categoria && <Typography variant="caption" color="error">{formErrors.categoria}</Typography>}
            </Grid>
            {formCategoria && camposDeCategoria(formCategoria).map(campo => (
              <Grid item size={6} xs={12} sm={6} key={campo.id}>
                <Typography fontWeight={600} color="textSecondary" gutterBottom>{campo.name}</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={campo.type === 'monto' ? formatNumberWithCommas(formCampos[campo.id] || '') : (formCampos[campo.id] || '')}
                  onChange={(e) => handleFormField(campo, e.target.value)}
                  placeholder={campo.name}
                  required={campo.required}
                  error={Boolean(formErrors[campo.id])}
                  helperText={formErrors[campo.id]}
                />
              </Grid>
            ))}
            {formDialog.mode === 'sustituir' && (
              <Grid item size={12} xs={12}>
                <Typography fontWeight={600} color="textSecondary" gutterBottom>Motivo (opcional)</Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Motivo de la sustitución"
                  value={formMotivo}
                  onChange={(e) => setFormMotivo(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={savingForm}>Cancelar</Button>
          <Button
            variant="contained"
            color={formDialog.mode === 'sustituir' ? 'warning' : 'primary'}
            onClick={submitForm}
            disabled={savingForm}
            startIcon={savingForm ? <CircularProgress size={16} color="inherit" /> : (formDialog.mode === 'sustituir' ? <SwapHorizOutlined /> : <AddOutlined />)}
          >
            {formDialog.mode === 'sustituir' ? 'Sustituir' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar garantía</DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>Esta acción no se puede deshacer.</Alert>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary">
              {`Se eliminará la garantía ${garantiaLabel(deleteTarget)}.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <GarantiaAdjuntos
        open={Boolean(adjuntosTarget)}
        garantia={adjuntosTarget}
        canEdit={puedeEditar}
        canDelete={puedeEliminar}
        onClose={() => setAdjuntosTarget(null)}
        onChanged={fetchGarantias}
      />
    </Box>
  );
}
